import React, { useState, useEffect, useRef } from "react";
import StructuralBackButton from "../shared/StructuralBackButton";
import { CustomIcon } from "../shared/CustomIcon";
import { useAppSettings } from "../../lib/AppSettingsContext";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";

// ============================================================
// QRPosterPage (admin) — swipeable carousel of printable QR posters
//   • Welcome, Attendance, Appointments, Food, Donations Listings,
//     Event-specific (Super Admin picks an event from a dropdown)
//   • Card title/subtitle/tagline are individually editable by Super Admin
//   • Each card has a Print button and a same-tab CTA link
// ============================================================
const DEFAULT_CARDS = (origin) => [
  { id: "welcome",      label: "Welcome Form",                    sub: "For new visitors",                 url: `${origin}/?welcome=1`,                  ctaLabel: "Open Welcome Form",       cta: `${origin}/?welcome=1`,                  tagline: "Scan to register your visit with us today." },
  { id: "attendance",   label: "Attendance Check-in",             sub: "Register for a service or event",  url: `${origin}/attendance`,                  ctaLabel: "Open Attendance Page",    cta: `${origin}/attendance`,                  tagline: "Scan to mark your attendance for today's service." },
  { id: "appointments", label: "Book an Appointment",             sub: "Book a pastoral consultation",     url: `${origin}/appointments/booking`,        ctaLabel: "Open Appointments Form",  cta: `${origin}/appointments/booking`,        tagline: "Scan to book a one-on-one appointment with a pastor." },
  { id: "food",         label: "Food Contribution Registration",  sub: "KGC Compassion",                   url: `${origin}/donations/food-registration`, ctaLabel: "Open Form",               cta: `${origin}/donations/food-registration`, tagline: "Scan to register your household for a food contribution." },
  { id: "listings",     label: "Donations Listings",              sub: "Browse available items",           url: `${origin}/donations/listings`,          ctaLabel: "Open Listings",           cta: `${origin}/donations/listings`,          tagline: "Scan to browse currently available donated items." },
];

export default function QRPosterPage({ currentUser, isSuperAdmin, events }) {
  const { settings: appSettings, reload: reloadAppSettings } = useAppSettings();
  const origin = window.location.origin;
  const overrides = (appSettings && appSettings.qrPosterCards) || {};

  // Per-event card — admin selects which event's QR to print.
  const upcomingEvents = (events || []).filter(e => e && e.id && e.name);
  const [eventCardId, setEventCardId] = useState(() => upcomingEvents[0]?.id || "");
  useEffect(() => {
    if (!eventCardId && upcomingEvents[0]) setEventCardId(upcomingEvents[0].id);
  }, [upcomingEvents.length, eventCardId]);
  const selectedEvent = upcomingEvents.find(e => e.id === eventCardId) || null;

  const baseCards = DEFAULT_CARDS(origin);
  const eventCard = {
    id: "event",
    label: selectedEvent ? selectedEvent.name : "Event Registration",
    sub: selectedEvent ? "Scan to register" : "Pick an event below",
    url: selectedEvent ? `${origin}/events/${selectedEvent.id}/register` : `${origin}/`,
    ctaLabel: "Open Registration Page",
    cta: selectedEvent ? `${origin}/events/${selectedEvent.id}/register` : `${origin}/`,
    tagline: selectedEvent ? `Scan to register for ${selectedEvent.name}.` : "Select an event from the dropdown to generate its QR code.",
  };
  // Merge per-card overrides (label / sub / tagline only).
  const cards = [...baseCards, eventCard].map(c => {
    const ov = overrides[c.id] || {};
    return {
      ...c,
      label:   ov.label   != null && ov.label   !== ""  ? ov.label   : c.label,
      sub:     ov.sub     != null && ov.sub     !== ""  ? ov.sub     : c.sub,
      tagline: ov.tagline != null && ov.tagline !== ""  ? ov.tagline : c.tagline,
    };
  });

  const [active, setActive] = useState(0);
  const [qrByUrl, setQrByUrl] = useState({});
  const [editingCard, setEditingCard] = useState(null); // card object being edited
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const QRCode = (await import("qrcode")).default;
        const next = {};
        for (const c of cards) {
          next[c.url] = await QRCode.toDataURL(c.url, {
            width: 720, margin: 1, errorCorrectionLevel: "H",
            color: { dark: "#000000", light: "#FFFFFF" },
          });
        }
        if (!cancelled) setQrByUrl(next);
      } catch (e) { console.error("QR generation failed", e); }
    })();
    return () => { cancelled = true; };
  }, [eventCardId, JSON.stringify(overrides)]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== active) setActive(i);
  };
  const goTo = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
    setActive(i);
  };
  const printCard = (cardId) => {
    document.body.dataset.printingCard = cardId;
    window.print();
    setTimeout(() => { delete document.body.dataset.printingCard; }, 500);
  };

  const saveCardText = async (cardId, patch) => {
    // Patch is partial: { label?, sub?, tagline? }
    const next = { ...overrides, [cardId]: { ...(overrides[cardId] || {}), ...patch } };
    try {
      const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ qrPosterCards: next }),
      });
      if (!r.ok) throw new Error("Could not save");
      await reloadAppSettings();
      setEditingCard(null);
    } catch (e) {
      alert(e.message || "Could not save");
    }
  };
  const resetCardText = async (cardId) => {
    const next = { ...overrides };
    delete next[cardId];
    const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ qrPosterCards: next }),
    });
    if (r.ok) {
      await reloadAppSettings();
      setEditingCard(null);
    } else {
      alert("Could not reset");
    }
  };

  return (
    <div data-testid="qr-poster-page">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body, #root, .layout, .main, .content { background: var(--kc-surface) !important; }
          .sidebar, .topbar, .qr-poster-controls, .bottom-nav-bar, .qr-carousel-dots, .qr-card-edit-btn, .qr-event-picker { display: none !important; }
          .content { padding: 0 !important; }
          .qr-carousel-scroller { overflow: visible !important; display: block !important; }
          .qr-poster-card { display: none !important; }
          body[data-printing-card] .qr-poster-card { display: none !important; }
          body[data-printing-card="welcome"]      .qr-poster-card[data-card-id="welcome"],
          body[data-printing-card="attendance"]   .qr-poster-card[data-card-id="attendance"],
          body[data-printing-card="appointments"] .qr-poster-card[data-card-id="appointments"],
          body[data-printing-card="food"]         .qr-poster-card[data-card-id="food"],
          body[data-printing-card="listings"]     .qr-poster-card[data-card-id="listings"],
          body[data-printing-card="event"]        .qr-poster-card[data-card-id="event"] {
            display: block !important; margin: 0 !important; box-shadow: none !important;
            border-radius: 0 !important; min-height: 100vh; width: 100% !important; max-width: none !important;
          }
          .qr-poster-card, .qr-poster-card * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .qr-carousel-scroller {
          display: flex; gap: 16px;
          overflow-x: auto; scroll-snap-type: x mandatory;
          scrollbar-width: none; -ms-overflow-style: none;
          padding: 4px 0 8px; -webkit-overflow-scrolling: touch;
        }
        .qr-carousel-scroller::-webkit-scrollbar { display: none; }
        .qr-poster-card {
          flex: 0 0 100%; scroll-snap-align: center;
          background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
          border-radius: 16px; padding: 36px 20px; text-align: center;
          position: relative; overflow: hidden;
          box-shadow: 0 24px 80px rgba(0,0,0,0.5);
        }
        .qr-carousel-dots { display: flex; justify-content: center; gap: 8px; margin: 14px 0 8px; }
        .qr-carousel-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: color-mix(in srgb, var(--kc-text) 25%, transparent); border: none; cursor: pointer;
          padding: 0; transition: all 0.2s;
        }
        .qr-carousel-dot.active { background: var(--kc-accent); width: 22px; border-radius: 999px; }
        .qr-card-print-btn { margin-top: 16px; }
        .qr-card-edit-btn {
          position: absolute; top: 12px; right: 12px;
          background: color-mix(in srgb, var(--kc-accent) 12%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent);
          color: var(--kc-accent); border-radius: 999px; padding: 5px 10px;
          font-size: 11px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.06em; cursor: pointer;
        }
        .qr-card-edit-btn:hover { background: color-mix(in srgb, var(--kc-accent) 22%, transparent); }
        .qr-event-picker { background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); color: var(--kc-text); border-radius: 8px; padding: 8px 10px; font-size: 13px; }
      `}</style>

      <div className="qr-poster-controls" style={{ marginBottom: 18 }}>
        <div style={{ marginBottom: 10 }}>
          <StructuralBackButton to="/dashboard" label="← Dashboard" testId="qr-poster-back" />
        </div>
        <div
          style={{ fontSize: 12, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif", textAlign: "center", lineHeight: 1.5 }}
          data-testid="qr-poster-subtitle"
        >
          Swipe to browse · tap a card&apos;s Print button for a clean single-page poster
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="qr-carousel-scroller"
        data-testid="qr-carousel"
        onScroll={onScroll}
      >
        {cards.map((c) => {
          const qrSrc = qrByUrl[c.url];
          return (
            <div key={c.id} className="qr-poster-card" data-testid={`qr-card-${c.id}`} data-card-id={c.id}>
              {isSuperAdmin && (
                <button
                  type="button"
                  className="qr-card-edit-btn"
                  data-testid={`qr-card-edit-${c.id}`}
                  onClick={() => setEditingCard({ id: c.id, label: c.label, sub: c.sub, tagline: c.tagline })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <CustomIcon name="edit" size={14} /> Edit Card Text
                </button>
              )}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                background: "radial-gradient(ellipse at 50% 0%, color-mix(in srgb, var(--kc-accent) 10%, transparent) 0%, transparent 55%), radial-gradient(ellipse at 50% 100%, color-mix(in srgb, var(--kc-accent) 6%, transparent) 0%, transparent 50%)",
              }} />
              <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
                <span aria-hidden="true" style={{ fontSize: 26, display: "inline-flex" }}><CustomIcon name="crown" size={28} /></span>
              </div>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.08em", lineHeight: 1.15, position: "relative" }}>
                {c.label}
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-accent)", fontStyle: "italic", letterSpacing: "0.10em", textTransform: "uppercase", marginTop: 8, position: "relative" }}>
                {c.sub}
              </div>

              {c.id === "event" && (
                <div className="qr-event-picker" style={{ position: "relative", margin: "16px auto 0", display: "inline-block" }}>
                  <select
                    data-testid="qr-event-picker"
                    value={eventCardId}
                    onChange={e => setEventCardId(e.target.value)}
                    className="qr-event-picker"
                  >
                    <option value="">— Select event —</option>
                    {upcomingEvents.map(ev => <option key={ev.id} value={ev.id}>{ev.name}{ev.date ? ` · ${ev.date}` : ""}</option>)}
                  </select>
                </div>
              )}

              <div style={{
                background: "#FFFFFF", borderRadius: 18, padding: 18,
                margin: "28px auto 22px",
                width: "min(280px, 70vw)", height: "min(280px, 70vw)",
                border: "3px solid var(--kc-accent)",
                boxShadow: "0 0 0 4px var(--kc-elevated), 0 0 0 5px color-mix(in srgb, var(--kc-accent) 40%, transparent)",
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative",
              }}>
                {qrSrc ? (
                  <img src={qrSrc} alt={`Scan to open ${c.label}`} data-testid={`qr-image-${c.id}`} style={{ width: "100%", height: "100%", display: "block" }} />
                ) : (
                  <div data-testid={`qr-image-loading-${c.id}`} style={{ color: "rgba(0,0,0,0.45)", fontFamily: "'Montserrat',sans-serif", fontSize: 12 }}>Generating QR…</div>
                )}
              </div>

              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "var(--kc-text)", lineHeight: 1.5, maxWidth: 360, margin: "0 auto 12px", position: "relative" }}>
                {c.tagline}
              </div>

              <a href={c.cta} className="btn btn-gold" data-testid={`qr-cta-${c.id}`} style={{ display: "inline-block", textDecoration: "none" }}>
                {c.ctaLabel}
              </a>

              <div className="qr-card-print-btn">
                <button type="button" className="btn btn-outline btn-sm" data-testid={`qr-print-${c.id}`} onClick={() => printCard(c.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <CustomIcon name="printer" size={14} /> Print this poster
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="qr-carousel-dots" data-testid="qr-carousel-dots">
        {cards.map((c, i) => (
          <button
            key={c.id}
            type="button"
            data-testid={`qr-dot-${i}`}
            aria-label={`Go to ${c.label}`}
            aria-current={active === i ? "true" : "false"}
            className={`qr-carousel-dot ${active === i ? "active" : ""}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>

      {editingCard && (
        <EditCardTextModal
          card={editingCard}
          onCancel={() => setEditingCard(null)}
          onSave={(patch) => saveCardText(editingCard.id, patch)}
          onReset={() => resetCardText(editingCard.id)}
        />
      )}
    </div>
  );
}

function EditCardTextModal({ card, onCancel, onSave, onReset }) {
  const [label, setLabel] = useState(card.label || "");
  const [sub, setSub] = useState(card.sub || "");
  const [tagline, setTagline] = useState(card.tagline || "");
  return (
    <div className="modal-overlay" data-testid="qr-edit-modal" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal cal-modal-scrollable" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div className="modal-title">Edit Card Text</div>
          <button className="modal-close" data-testid="qr-edit-close" onClick={onCancel}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group mb-14">
            <label>Title</label>
            <input data-testid="qr-edit-label" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. WELCOME FORM" />
          </div>
          <div className="form-group mb-14">
            <label>Subtitle</label>
            <input data-testid="qr-edit-sub" value={sub} onChange={e => setSub(e.target.value)} placeholder="e.g. For new visitors" />
          </div>
          <div className="form-group mb-14">
            <label>Instruction text below QR code</label>
            <textarea data-testid="qr-edit-tagline" rows={3} value={tagline} onChange={e => setTagline(e.target.value)} placeholder="e.g. Scan to register your visit with us today." />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" data-testid="qr-edit-reset" onClick={onReset}>Reset to Default</button>
          <button type="button" className="btn btn-outline" data-testid="qr-edit-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-gold" data-testid="qr-edit-save" onClick={() => onSave({ label, sub, tagline })}>Save</button>
        </div>
      </div>
    </div>
  );
}
